import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { indicatorCatalogue } from "./catalogue";
import {
  FORM_VERSION,
  indicatorExplanations,
  openingBlock,
  organisationTypes,
  weightingBasisPointers,
  yearsExperienceBands,
} from "./config";
import { deleteDraft, loadDraft, saveDraft } from "./draftStore";
import {
  createDraft,
  emptyResponse,
  profileErrors,
  summariseDraft,
} from "./logic";
import type {
  A1Draft,
  A1ItemResponse,
  A1Profile,
  AvailabilityView,
} from "./types";
import {
  createStage2State,
  normalizeStage2State,
  withDraftMetadata,
} from "./stage2/logic";
import { loadStage2State, saveStage2State } from "./stage2/store";
import type { Stage2State } from "./stage2/types";

type SaveState = "loading" | "saving" | "saved" | "error";

const Stage2Router = lazy(() => import("./stage2/Stage2Router"));
const Stage3Router = lazy(() => import("./stage3/Stage3Router"));
const portableReview = import.meta.env.VITE_PORTABLE_REVIEW === "true";
const selfContainedNavigation =
  portableReview || import.meta.env.VITE_SELF_CONTAINED === "true";

function routeFromLocation() {
  if (selfContainedNavigation) {
    const hashRoute = window.location.hash.replace(/^#/, "").split("?")[0];
    return hashRoute || "/";
  }
  const path = window.location.pathname.replace(/\/+$/, "");
  const base = "/wsi-input";
  const route = path.startsWith(base) ? path.slice(base.length) : path;
  return route || "/";
}

function navigate(route: string) {
  if (selfContainedNavigation) {
    if (window.location.hash === `#${route}`) {
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } else {
      window.location.hash = route;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const nextPath = `/wsi-input${route === "/" ? "/" : route}`;
  window.history.pushState({}, "", nextPath);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`field ${error ? "field--error" : ""}`}>
      <div className="field__label">
        {label}
        <span>{required ? "Required" : "Optional"}</span>
      </div>
      {hint && <p className="field__hint">{hint}</p>}
      {children}
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function OpeningBlock() {
  return (
    <section className="opening-block" aria-labelledby="opening-title">
      <p className="eyebrow" id="opening-title">
        Before you begin
      </p>
      <ol>
        {openingBlock.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ol>
    </section>
  );
}

function WeightingBasisGuide({ onClose }: { onClose: () => void }) {
  return (
    <div className="guide-backdrop" role="presentation" onClick={onClose}>
      <section
        className="basis-guide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="basis-guide-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="help-panel__close"
          onClick={onClose}
          aria-label="Close weighting basis guide"
        >
          ×
        </button>
        <p className="eyebrow">Weighting guidance</p>
        <h2 id="basis-guide-title">Use a defensible basis for each rating</h2>
        <p className="basis-guide__intro">
          These are decision clues, not automatic answers. Apply them with your
          professional knowledge and explain where local conditions lead you to
          a different conclusion.
        </p>
        <div className="basis-list">
          {weightingBasisPointers.map((pointer, index) => (
            <article key={pointer.title}>
              <span>{index + 1}</span>
              <div>
                <h3>{pointer.title}</h3>
                <p>{pointer.detail}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="basis-scale">
          <strong>Practical scale</strong>
          <span>1–3: weak or indirect basis</span>
          <span>4–7: relevant but context-dependent</span>
          <span>8–10: core, direct and decision-useful</span>
        </div>
        <button className="button button--primary" onClick={onClose}>
          Apply these pointers
        </button>
      </section>
    </div>
  );
}

function ContextBar({
  draft,
  saveState,
  online,
}: {
  draft: A1Draft;
  saveState: SaveState;
  online: boolean;
}) {
  const saveLabel = {
    loading: "Opening draft…",
    saving: "Saving on device…",
    saved: "Saved on this device",
    error: "Local save needs attention",
  }[saveState];

  return (
    <div className="context-bar" aria-label="Assignment context">
      <div>
        <span>Assignment 1</span>
        <strong>
          {draft.profile.municipalityCode || "Area not selected"}
        </strong>
      </div>
      <div className="context-bar__states">
        <span className={`connection ${online ? "is-online" : "is-offline"}`}>
          {online ? "Online" : "Offline"}
        </span>
        <span className={`save-state save-state--${saveState}`}>{saveLabel}</span>
      </div>
    </div>
  );
}

function AppHeader({
  onHelp,
  onHome,
  showNavigation,
}: {
  onHelp: () => void;
  onHome: () => void;
  showNavigation: boolean;
}) {
  return (
    <header className="app-header">
      <button className="brand" onClick={onHome} aria-label="WSI input home">
        <span className="brand__mark">WSI</span>
        <span>
          <strong>Field Inputs</strong>
          <small>Student support portal</small>
        </span>
      </button>
      <div className="app-header__actions">
        {showNavigation && (
          <>
            <button
              className="button button--quiet button--small"
              onClick={() => window.history.back()}
            >
              ← Back
            </button>
            <button
              className="button button--quiet button--small"
              onClick={onHome}
            >
              Home
            </button>
          </>
        )}
        <button className="button button--quiet button--small" onClick={onHelp}>
          Help
        </button>
      </div>
    </header>
  );
}

function StepRail({ active }: { active: "profile" | "items" | "review" }) {
  const steps = [
    ["profile", "Profile"],
    ["items", "Weighting"],
    ["review", "Review"],
  ] as const;
  const activeIndex = steps.findIndex(([id]) => id === active);
  return (
    <nav className="step-rail" aria-label="Assignment progress">
      {steps.map(([id, label], index) => (
        <button
          key={id}
          className={index <= activeIndex ? "is-active" : ""}
          onClick={() => {
            if (index <= activeIndex) navigate(`/a1/${id}`);
          }}
          disabled={index > activeIndex}
        >
          <span>{index + 1}</span>
          {label}
        </button>
      ))}
    </nav>
  );
}

function Landing({
  draft,
  onNewDraft,
}: {
  draft: A1Draft;
  onNewDraft: () => void;
}) {
  const hasProgress =
    Object.keys(draft.responses).length > 0 ||
    Boolean(draft.profile.organisationName) ||
    draft.profile.consentToParticipate;
  return (
    <main className="landing">
      <section className="hero">
        <div>
          <p className="eyebrow">WRC Project C2024/2025-01599</p>
          <h1>Contribute evidence to the Water Security Index</h1>
          <p className="hero__lede">
            One central portal for indicator weighting, infrastructure capture
            and controlled WSI review.
          </p>
        </div>
      </section>

      <section
        className="development-stage-hub"
        aria-labelledby="development-stage-title"
      >
        <header>
          <div>
            <p className="eyebrow">Complete student support journey</p>
            <h2 id="development-stage-title">
              Choose the stage you are reviewing.
            </h2>
          </div>
          <p>
            Each stage has a dedicated browser page. Return here through the
            WSI header at any time.
          </p>
        </header>
        <div className="development-stage-grid">
          <article className="development-stage-card development-stage-card--one">
            <span>Stage 1</span>
            <h3>Indicator weighting</h3>
            <p>
              Complete the professional profile, rate the 67 WSI indicators
              and review the local submission receipt.
            </p>
            <button
              className="button button--primary"
              onClick={() => navigate(hasProgress ? "/a1/items" : "/a1/profile")}
            >
              {hasProgress ? "Continue weighting" : "Open weighting form"}
            </button>
          </article>
          <article className="development-stage-card development-stage-card--two">
            <span>Stage 2</span>
            <h3>Field capture and water provision</h3>
            <p>
              Locate infrastructure, record its Quick Facts and complete the
              official or community water-provision exercises.
            </p>
            <button
              className="button button--primary"
              onClick={() => navigate("/a2/asset")}
            >
              Open Stage 2
            </button>
          </article>
          <article className="development-stage-card development-stage-card--three">
            <span>Stage 3</span>
            <h3>Controlled review and release</h3>
            <p>
              Review synchronisation, quality decisions, authorised map layers
              and checksum-verified WSI release packages.
            </p>
            <button
              className="button button--primary"
              onClick={() => navigate("/stage3")}
            >
              Open Stage 3
            </button>
          </article>
        </div>
      </section>

      <section className="assignment-grid" aria-label="Assignments">
        <article className="assignment-card assignment-card--active">
          <div className="assignment-card__topline">
            <span>A1</span>
            <span className="pill pill--ready">Ready to review</span>
          </div>
          <h2>Weight the WSI indicators</h2>
          <p>
            Help decide which Water Security Index indicators should matter
            most. Rate each indicator from 1 to 10. Leave an item blank if you
            do not have a basis to judge it.
          </p>
          <div className="assignment-card__meta">
            <span>67 indicators</span>
            <span>20–30 minutes</span>
            <span>Works offline after opening</span>
          </div>
          <div className="assignment-card__actions">
            <button
              className="button button--primary"
              onClick={() => navigate(hasProgress ? "/a1/items" : "/a1/profile")}
            >
              {hasProgress ? "Continue weighting" : "Start weighting"}
            </button>
            {hasProgress && (
              <button className="button button--quiet" onClick={onNewDraft}>
                Start a new local draft
              </button>
            )}
          </div>
        </article>

        <article className="assignment-card assignment-card--stage2">
          <div className="assignment-card__topline">
            <span>A2</span>
            <span className="pill pill--stage2">Stage 2 ready</span>
          </div>
          <h2>Complete infrastructure map missions</h2>
          <p>
            Find an item first, drop its symbol onto the map and add only the
            quick facts that matter for that type.
          </p>
          <div className="assignment-card__meta assignment-card__meta--light">
            <span>Location first</span>
            <span>31 primary types + Other</span>
            <span>CSV / XLSX import</span>
          </div>
          <button className="button button--primary" onClick={() => navigate("/a2/asset")}>
            Start a map mission
          </button>
        </article>

        <article className="assignment-card assignment-card--stage2">
          <div className="assignment-card__topline">
            <span>A3</span>
            <span className="pill pill--stage2">Stage 2 ready</span>
          </div>
          <h2>Describe water provision</h2>
          <p>
            Complete a separate official assessment or a community
            experience form, with the required privacy safeguards.
          </p>
          <div className="assignment-card__meta assignment-card__meta--light">
            <span>Official assessment</span>
            <span>Community experience form</span>
          </div>
          <button className="button button--primary" onClick={() => navigate("/a3")}>
            Choose assessment
          </button>
        </article>
      </section>
      <section className="stage3-launch">
        <div>
          <p className="eyebrow">Stage 3 · controlled collaboration</p>
          <h2>Ready to move completed fieldwork into review?</h2>
          <p>
            Open the secure control centre to sync finished missions, resolve
            quality questions and prepare checksum-verified WSI outputs.
          </p>
        </div>
        <button
          className="button button--primary"
          onClick={() => navigate("/stage3")}
        >
          Open Stage 3 control centre
        </button>
      </section>
    </main>
  );
}

function ProfileScreen({
  draft,
  onChange,
}: {
  draft: A1Draft;
  onChange: (profile: A1Profile) => void;
}) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [basisGuideOpen, setBasisGuideOpen] = useState(false);
  const profile = draft.profile;
  const update = <K extends keyof A1Profile>(key: K, value: A1Profile[K]) =>
    onChange({ ...profile, [key]: value });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = profileErrors(profile);
    setErrors(nextErrors);
    if (nextErrors.contact) {
      window.alert("Please enter valid SA number.");
      return;
    }
    if (Object.keys(nextErrors).length === 0) navigate("/a1/items");
  };


  return (
    <main className="form-page">
      <StepRail active="profile" />
      <OpeningBlock />
      <section className="page-heading">
        <p className="eyebrow">Respondent profile</p>
        <h1>Tell us about the basis for your ratings</h1>
        <button
          className="insight-button"
          type="button"
          onClick={() => setBasisGuideOpen(true)}
        >
          <span>i</span>
          See seven established weighting considerations
        </button>
      </section>

      <form className="form-stack" onSubmit={handleSubmit} noValidate>
        {Object.keys(errors).length > 0 && (
          <div className="error-summary" role="alert">
            <strong>Check the highlighted fields</strong>
            <span>{Object.keys(errors).length} item(s) need attention.</span>
          </div>
        )}

        <Field
          label="Participation"
          required
          error={errors.consentToParticipate}
        >
          <label className="check-card">
            <input
              type="checkbox"
              checked={profile.consentToParticipate}
              onChange={(event) =>
                update("consentToParticipate", event.target.checked)
              }
            />
            <span>
              I understand the purpose and voluntarily provide these
              professional views for the WSI project.
            </span>
          </label>
        </Field>

        <Field label="How is this response being completed?" required>
          <div className="segmented">
            <label>
              <input
                type="radio"
                name="completionMode"
                checked={profile.completionMode === "self"}
                onChange={() => update("completionMode", "self")}
              />
              <span>By the respondent</span>
            </label>
            <label>
              <input
                type="radio"
                name="completionMode"
                checked={profile.completionMode === "student-assisted"}
                onChange={() => update("completionMode", "student-assisted")}
              />
              <span>Entered from a call / consultation</span>
            </label>
          </div>
        </Field>

        {profile.completionMode === "student-assisted" && (
          <div className="form-grid">
            <Field label="Respondent role" required error={errors.sourceRole}>
              <input
                value={profile.sourceRole}
                onChange={(event) => update("sourceRole", event.target.value)}
                placeholder="e.g. Municipal water services manager"
              />
            </Field>
            <Field
              label="Respondent organisation"
              required
              error={errors.sourceOrganisation}
            >
              <input
                value={profile.sourceOrganisation}
                onChange={(event) =>
                  update("sourceOrganisation", event.target.value)
                }
                placeholder="Organisation supplying the information"
              />
            </Field>
          </div>
        )}

        <div className="form-grid">
          <Field
            label="Organisation of Respondent"
            required
            error={errors.organisationName}
          >
            <input
              value={profile.organisationName}
              onChange={(event) =>
                update("organisationName", event.target.value)
              }
              autoComplete="organization"
            />
          </Field>
          <Field
            label="Organisation type"
            required
            error={errors.organisationType}
          >
            <select
              value={profile.organisationType}
              onChange={(event) =>
                update("organisationType", event.target.value)
              }
            >
              <option value="">Choose a type</option>
              {organisationTypes.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Professional role"
            required
            error={errors.professionalRole}
            hint="Role or area of responsibility, not a CV."
          >
            <input
              value={profile.professionalRole}
              onChange={(event) =>
                update("professionalRole", event.target.value)
              }
            />
          </Field>
          <Field label="Years of experience">
            <select
              value={profile.yearsExperienceBand}
              onChange={(event) =>
                update("yearsExperienceBand", event.target.value)
              }
            >
              <option value="">Choose a band</option>
              {yearsExperienceBands.map((band) => (
                <option key={band}>{band}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="form-grid">
          <Field label="Experience mainly applies at" required>
            <select
              value={profile.geographicExperienceType}
              onChange={(event) =>
                update(
                  "geographicExperienceType",
                  event.target.value as A1Profile["geographicExperienceType"],
                )
              }
            >
              <option value="national">National level</option>
              <option value="province">Province</option>
              <option value="municipality">Municipality / local area</option>
            </select>
          </Field>
          <Field
            label="Main geographic area"
            required
            error={errors.geographicExperienceDetail}
          >
            <input
              value={profile.geographicExperienceDetail}
              onChange={(event) =>
                update("geographicExperienceDetail", event.target.value)
              }
              placeholder="e.g. Gauteng, national, or municipality name"
            />
          </Field>
          <Field
            label="Municipality / area this weighting applies to"
            required
            error={errors.municipalityCode}
            hint="Type the municipality or area your answers should be grouped under — or write National when they are not for one municipality."
          >
            <input
              value={profile.municipalityCode}
              onChange={(event) =>
                update("municipalityCode", event.target.value)
              }
              placeholder="e.g. Letsemeng Local Municipality, or National"
            />
          </Field>
        </div>

        <div className="form-grid">
          <Field
            label="Respondent name"
            required
            error={errors.respondentName}
          >
            <input
              value={profile.respondentName}
              onChange={(event) =>
                update("respondentName", event.target.value)
              }
              autoComplete="name"
            />
          </Field>
          <Field
            label="Phone number"
            required
            error={errors.contact}
            hint="Landline or cellphone. Kept with the protected record only."
          >
            <input
              inputMode="tel"
              value={profile.contact}
              onChange={(event) => update("contact", event.target.value)}
              autoComplete="tel"
            />
          </Field>
        </div>

        <div className="sticky-actions">
          <span>Your answers autosave on this device.</span>
          <button className="button button--primary" type="submit">
            Continue to 67 indicators
          </button>
        </div>
      </form>
      {basisGuideOpen && (
        <WeightingBasisGuide onClose={() => setBasisGuideOpen(false)} />
      )}
    </main>
  );
}

function WeightScale({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
}) {
  return (
    <fieldset className="weight-scale">
      <legend>Your weight</legend>
      <div className="weight-scale__labels" aria-hidden="true">
        <span>Low importance</span>
        <span>High importance</span>
      </div>
      <div className="weight-scale__buttons">
        {Array.from({ length: 10 }, (_, index) => index + 1).map((weight) => (
          <button
            key={weight}
            type="button"
            className={value === weight ? "is-selected" : ""}
            onClick={() => onChange(weight)}
            aria-pressed={value === weight}
            aria-label={`Weight ${weight} out of 10`}
          >
            {weight}
          </button>
        ))}
      </div>
      <button
        className="blank-button"
        type="button"
        onClick={() => onChange(null)}
      >
        Leave blank — no basis to judge
      </button>
    </fieldset>
  );
}

function ItemsScreen({
  draft,
  onChange,
}: {
  draft: A1Draft;
  onChange: (draft: A1Draft) => void;
}) {
  const indicators = indicatorCatalogue.indicators;
  const [basisGuideOpen, setBasisGuideOpen] = useState(false);
  const index = Math.min(draft.currentIndex, indicators.length - 1);
  const indicator = indicators[index];
  const response = draft.responses[indicator.indicatorId] ?? emptyResponse();
  const scoped = draft.scopedDimensions.length
    ? draft.scopedDimensions.includes(indicator.indicatorId.slice(0, 2))
    : true;
  const progress = ((index + 1) / indicators.length) * 100;
  const difference =
    response.weight == null
      ? null
      : Math.abs(response.weight - indicator.proposedWeight);

  const updateResponse = (patch: Partial<A1ItemResponse>) => {
    const nextResponse = { ...response, ...patch };
    onChange({
      ...draft,
      responses: {
        ...draft.responses,
        [indicator.indicatorId]: nextResponse,
      },
      updatedAt: new Date().toISOString(),
    });
  };

  const move = (nextIndex: number) => {
    const bounded = Math.max(0, Math.min(indicators.length - 1, nextIndex));
    onChange({ ...draft, currentIndex: bounded });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="form-page form-page--item">
      <StepRail active="items" />
      <div className="item-progress">
        <div>
          <span>
            Indicator {index + 1} of {indicators.length}
          </span>
          <strong>{Math.round(progress)}% viewed</strong>
        </div>
        <div className="progress-track" aria-label={`${Math.round(progress)}%`}>
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>

      <article className="indicator-card">
        <div className="indicator-card__header">
          <div>
            <div className="indicator-tags">
              <span className={`priority priority--${indicator.priorityGroup.toLowerCase()}`}>
                {indicator.priorityGroup} proposed importance
              </span>
              {!scoped && <span className="pill">Outside invited scope</span>}
              <button
                className="indicator-guide-button"
                onClick={() => setBasisGuideOpen(true)}
                type="button"
              >
                Weighting basis guide
              </button>
            </div>
            <p className="eyebrow">
              {indicator.indicatorId} · {indicator.dimension}
            </p>
            <h1>{indicator.indicatorName}</h1>
          </div>
          <div className="proposed-weight">
            <span>Project proposal</span>
            <strong>{indicator.proposedWeight}</strong>
            <small>out of 10</small>
          </div>
        </div>

        <div className="indicator-facts">
          <div>
            <span>How measured</span>
            <strong>{indicator.measurementUnit}</strong>
          </div>
          <div>
            <span>Direction</span>
            <strong>{indicator.directionSentence}</strong>
            {indicatorExplanations[indicator.indicatorId] && (
              <details className="plain-explanation">
                <summary>What does this mean?</summary>
                <p>{indicatorExplanations[indicator.indicatorId]}</p>
              </details>
            )}
          </div>
          <div>
            <span>Project data status</span>
            <strong>{indicator.projectDataStatus}</strong>
          </div>
        </div>

        <div className="guidance-strip">
          <span>
            <strong>1–3</strong> Low
          </span>
          <span>
            <strong>4–7</strong> Medium
          </span>
          <span>
            <strong>8–10</strong> High
          </span>
          <span>
            <strong>Blank</strong> No basis
          </span>
        </div>

        <WeightScale
          value={response.weight}
          onChange={(weight) => updateResponse({ weight })}
        />

        <div className="response-fields">
          <Field
            label="Your view of data availability"
            hint="The project’s own data status is shown above for comparison."
          >
            <select
              value={response.availabilityView}
              onChange={(event) =>
                updateResponse({
                  availabilityView: event.target.value as AvailabilityView,
                })
              }
            >
              <option value="">No answer</option>
              <option>Yes</option>
              <option>Partly</option>
              <option>No</option>
              <option>Unsure</option>
            </select>
          </Field>
          <Field
            label="Comment"
            hint={
              difference != null && difference >= 3
                ? "Your rating differs substantially from the project proposal. A brief explanation is especially useful."
                : "Briefly explain a different rating, data concern or possible overlap."
            }
          >
            <textarea
              value={response.comment}
              onChange={(event) =>
                updateResponse({ comment: event.target.value })
              }
              rows={4}
            />
          </Field>
        </div>
        <div className="indicator-card__actions">
          <span>
            {response.weight == null
              ? "This indicator will remain blank."
              : `Weight ${response.weight} is saved on this device.`}
          </span>
          {index < indicators.length - 1 ? (
            <button
              className="button button--primary"
              onClick={() => move(index + 1)}
            >
              Save and next
            </button>
          ) : (
            <button
              className="button button--primary"
              onClick={() => navigate("/a1/review")}
            >
              Review responses
            </button>
          )}
        </div>
      </article>

      <div className="item-nav">
        <button
          className="button button--quiet"
          onClick={() => move(index - 1)}
          disabled={index === 0}
        >
          Previous
        </button>
        <label className="jump-control">
          <span>Jump to</span>
          <select
            value={index}
            onChange={(event) => move(Number(event.target.value))}
          >
            {indicators.map((item, itemIndex) => (
              <option key={item.indicatorId} value={itemIndex}>
                {itemIndex + 1}. {item.indicatorId} — {item.indicatorName}
              </option>
            ))}
          </select>
        </label>
      </div>

      <button className="review-shortcut" onClick={() => navigate("/a1/review")}>
        Review at any time
      </button>
      {basisGuideOpen && (
        <WeightingBasisGuide onClose={() => setBasisGuideOpen(false)} />
      )}
    </main>
  );
}

function ReviewScreen({
  draft,
  onComplete,
  onEdit,
}: {
  draft: A1Draft;
  onComplete: () => void;
  onEdit: (index: number) => void;
}) {
  const summary = summariseDraft(draft, indicatorCatalogue.indicators);
  const answeredItems = indicatorCatalogue.indicators.filter((indicator) => {
    const response = draft.responses[indicator.indicatorId];
    return response?.weight != null || response?.comment.trim();
  });
  const lowCompletion = summary.scopedCompletion < 0.7;
  return (
    <main className="form-page review-page">
      <StepRail active="review" />
      <section className="page-heading">
        <p className="eyebrow">Review before completion</p>
        <h1>Your weighting summary</h1>
        <p>
          Blanks are valid and remain null. They are not converted to zero or
          to the proposed project weight.
        </p>
      </section>

      <section className="summary-grid" aria-label="Response summary">
        <div>
          <span>Weighted</span>
          <strong>{summary.weightedCount}</strong>
          <small>of 67 indicators</small>
        </div>
        <div>
          <span>Left blank</span>
          <strong>{summary.blankCount}</strong>
          <small>valid no-basis responses</small>
        </div>
        <div>
          <span>Average</span>
          <strong>
            {summary.averageWeight == null
              ? "—"
              : summary.averageWeight.toFixed(1)}
          </strong>
          <small>entered weights only</small>
        </div>
        <div>
          <span>Comments</span>
          <strong>{summary.commentedCount}</strong>
          <small>{summary.differentCount} differ from proposal</small>
        </div>
      </section>

      {draft.scopedDimensions.length > 0 && (
        <section className="scope-summary">
          <div>
            <span>Invited scope</span>
            <strong>{draft.scopedDimensions.join(", ")}</strong>
          </div>
          <div>
            <span>Scoped completion</span>
            <strong>{Math.round(summary.scopedCompletion * 100)}%</strong>
            <small>
              {summary.scopedWeightedCount} of {summary.scopedItemCount}
            </small>
          </div>
        </section>
      )}

      {lowCompletion && (
        <div className="quality-flag">
          <strong>Low-completion quality flag</strong>
          <p>
            Fewer than 70% of indicators in scope have a weight. You may still
            complete the form because every item is optional.
          </p>
        </div>
      )}

      <section className="review-list">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Entered responses</p>
            <h2>Check weights and comments</h2>
          </div>
          <button
            className="button button--quiet"
            onClick={() => navigate("/a1/items")}
          >
            Return to indicators
          </button>
        </div>
        {answeredItems.length ? (
          answeredItems.map((indicator) => {
            const response = draft.responses[indicator.indicatorId];
            const index = indicatorCatalogue.indicators.indexOf(indicator);
            return (
              <button
                className="review-row"
                key={indicator.indicatorId}
                onClick={() => onEdit(index)}
              >
                <span>
                  <strong>{indicator.indicatorId}</strong>
                  {indicator.indicatorName}
                </span>
                <span>
                  Your weight
                  <strong>{response.weight ?? "Blank"}</strong>
                </span>
                <span>
                  Proposed
                  <strong>{indicator.proposedWeight}</strong>
                </span>
                <span>{response.comment ? "Comment added" : "Edit"}</span>
              </button>
            );
          })
        ) : (
          <div className="empty-state">
            <strong>No weights entered yet</strong>
            <p>
              That is allowed, but return to the indicators if you intended to
              contribute ratings.
            </p>
          </div>
        )}
      </section>

      <section className="completion-panel">
        <button className="button button--primary" onClick={onComplete}>
          Complete Stage 1 review
        </button>
      </section>
    </main>
  );
}

function ReceiptScreen({ draft }: { draft: A1Draft }) {
  const summary = summariseDraft(draft, indicatorCatalogue.indicators);
  if (!draft.receipt) {
    return (
      <main className="not-found">
        <p className="eyebrow">No local receipt yet</p>
        <h1>Complete the review before opening an acknowledgement.</h1>
        <button
          className="button button--primary"
          onClick={() => navigate("/a1/review")}
        >
          Go to review
        </button>
      </main>
    );
  }
  return (
    <main className="receipt-page">
      <section className="receipt-card">
        <div className="receipt-icon" aria-hidden="true">
          ✓
        </div>
        <p className="eyebrow">Stage 1 review completed</p>
        <h1>Your local receipt is ready</h1>
        <p>
          This confirms the flow was completed on this device. It is not a
          server submission and is not yet in the project review queue.
        </p>
        <dl>
          <div>
            <dt>Reference</dt>
            <dd>{draft.receipt.reference}</dd>
          </div>
          <div>
            <dt>Completed</dt>
            <dd>
              {new Date(draft.receipt.completedAt).toLocaleString("en-ZA", {
                dateStyle: "long",
                timeStyle: "short",
              })}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>Saved on this device — server sync not connected</dd>
          </div>
          <div>
            <dt>Catalogue</dt>
            <dd>{draft.catalogueVersion}</dd>
          </div>
          <div>
            <dt>Respondent</dt>
            <dd>
              {draft.profile.respondentName.trim() ||
                `${draft.profile.professionalRole || "Professional respondent"} · ${
                  draft.profile.organisationName || "Organisation not recorded"
                }`}
            </dd>
          </div>
          <div>
            <dt>Area</dt>
            <dd>
              {draft.profile.municipalityCode ||
                draft.profile.geographicExperienceDetail}
            </dd>
          </div>
          <div>
            <dt>Completion mode</dt>
            <dd>
              {draft.profile.completionMode === "student-assisted"
                ? "Entered from a call / consultation"
                : "Completed by the respondent"}
            </dd>
          </div>
          <div>
            <dt>Weighted / blank</dt>
            <dd>
              {summary.weightedCount} / {summary.blankCount}
            </dd>
          </div>
        </dl>
        <div className="receipt-actions">
          <button
            className="button button--primary"
            onClick={() => {
              sessionStorage.setItem("wsi-a1-autosend", "1");
              navigate("/send");
            }}
          >
            Send to the project server now
          </button>
          <button className="button button--quiet" onClick={() => window.print()}>
            Print acknowledgement
          </button>
          <button className="button button--quiet" onClick={() => navigate("/")}>
            Back to assignments
          </button>
        </div>
      </section>
    </main>
  );
}

function HelpPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className="help-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="help-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="help-panel__close"
          onClick={onClose}
          aria-label="Close help"
        >
          ×
        </button>
        <p className="eyebrow">Help</p>
        <h2 id="help-title">Using this review build</h2>
        <h3>Saving your work</h3>
        <p>
          “Saved on this device” means your work is safe in this browser but
          may not yet be on the project server. Stage 1 does not connect server
          sync.
        </p>
        <h3>Leaving an item blank</h3>
        <p>
          Leave blank if you do not have a basis to judge an indicator. Blank
          is not the same as a weight of 1.
        </p>
        <h3>Report a problem</h3>
        <p>
          Note the page, device and browser. Do not include response content in
          a support message.
        </p>
        <a
          className="button button--primary"
          href={`mailto:?subject=${encodeURIComponent(
            `WSI input Stage 1 problem — ${FORM_VERSION}`,
          )}`}
        >
          Prepare support email
        </a>
      </aside>
    </div>
  );
}

function Stage2Nav({ route }: { route: string }) {
  const items = [
    { route: "/a2/asset", label: "Map mission" },
    { route: "/a2/map", label: "Team map" },
    { route: "/a2/import", label: "Bulk import" },
    { route: "/a3", label: "A3 forms" },
    { route: "/engagements", label: "Engagements" },
    { route: "/send", label: "Send to project" },
    { route: "/details", label: "My details" },
    { route: "/dev/symbols", label: "Symbol gallery" },
    { route: "/stage3", label: "Stage 3" },
  ];
  return (
    <nav className="stage2-nav" aria-label="Stage 2 screens">
      {items.map((item) => (
        <button
          key={item.route}
          className={
            route === item.route ||
            (item.route === "/a2/asset" && route.startsWith("/a2/asset/")) ||
            (item.route === "/a3" && route.startsWith("/a3/")) ||
            (item.route === "/stage3" && route.startsWith("/stage3/"))
              ? "is-active"
              : ""
          }
          onClick={() => navigate(item.route)}
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}

export default function App() {
  const [route, setRoute] = useState(routeFromLocation());
  const [draft, setDraft] = useState<A1Draft>(() =>
    createDraft(
      indicatorCatalogue.catalogueVersion,
      FORM_VERSION,
      new URLSearchParams(window.location.search)
        .get("scope")
        ?.split(",")
        .filter((value) => /^D[1-8]$/.test(value)) ?? [],
    ),
  );
  const [loaded, setLoaded] = useState(false);
  const [stage2State, setStage2State] = useState<Stage2State>(() =>
    createStage2State(),
  );
  const [stage2Loaded, setStage2Loaded] = useState(false);
  const [stage2SaveState, setStage2SaveState] =
    useState<"saved" | "saving" | "error">("saved");
  const stage2StateRef = useRef(stage2State);
  const [saveState, setSaveState] = useState<SaveState>("loading");
  const [online, setOnline] = useState(navigator.onLine);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const handleRoute = () => setRoute(routeFromLocation());
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("popstate", handleRoute);
    window.addEventListener("hashchange", handleRoute);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("popstate", handleRoute);
      window.removeEventListener("hashchange", handleRoute);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    loadStage2State()
      .then((storedState) => {
        if (storedState) setStage2State(normalizeStage2State(storedState));
      })
      .finally(() => setStage2Loaded(true));
  }, []);

  useEffect(() => {
    loadDraft()
      .then((storedDraft) => {
        if (
          storedDraft &&
          storedDraft.catalogueVersion === indicatorCatalogue.catalogueVersion
        ) {
          setDraft(storedDraft);
        }
        setSaveState("saved");
      })
      .catch(() => setSaveState("error"))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!loaded) return;
    setSaveState("saving");
    const timer = window.setTimeout(() => {
      saveDraft({ ...draft, updatedAt: new Date().toISOString() })
        .then(() => setSaveState("saved"))
        .catch(() => setSaveState("error"));
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, loaded]);

  useEffect(() => {
    if (!stage2Loaded) return;
    setStage2SaveState("saving");
    const timer = window.setTimeout(() => {
      const stamped = { ...stage2State, updatedAt: new Date().toISOString() };
      // Resilient save: one retry, then a degraded save without evidence
      // blobs (field data survives a full storage; photos re-attach later).
      const stripBlobs = (state: Stage2State): Stage2State => ({
        ...state,
        a2Draft: {
          ...state.a2Draft,
          evidence: state.a2Draft.evidence.map(({ blob: _blob, ...item }) => item),
        },
        assets: state.assets.map((asset) => ({
          ...asset,
          evidence: asset.evidence.map(({ blob: _blob, ...item }) => item),
        })),
      });
      saveStage2State(stamped)
        .catch(() => saveStage2State(stamped))
        .catch(() => saveStage2State(stripBlobs(stamped)))
        .then(() => setStage2SaveState("saved"))
        .catch(() => {
          setStage2SaveState("error");
          setStage2State((current) => {
            if (
              !current.a2Draft.evidence.some(
                (item) => item.state === "ready_to_upload",
              )
            ) {
              return current;
            }
            return {
              ...current,
              a2Draft: {
                ...current.a2Draft,
                evidence: current.a2Draft.evidence.map((item) =>
                  item.state === "ready_to_upload"
                    ? { ...item, state: "local_only" as const }
                    : item,
                ),
              },
            };
          });
        });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [stage2State, stage2Loaded]);

  useEffect(() => {
    stage2StateRef.current = stage2State;
  }, [stage2State]);

  useEffect(() => {
    if (!stage2Loaded) return;
    const flush = () => {
      if (document.visibilityState === "hidden") {
        void saveStage2State({
          ...stage2StateRef.current,
          updatedAt: new Date().toISOString(),
        }).catch(() => {
          setStage2SaveState("error");
          setStage2State((current) => {
            if (
              !current.a2Draft.evidence.some(
                (item) => item.state === "ready_to_upload",
              )
            ) {
              return current;
            }
            return {
              ...current,
              a2Draft: {
                ...current.a2Draft,
                evidence: current.a2Draft.evidence.map((item) =>
                  item.state === "ready_to_upload"
                    ? { ...item, state: "local_only" as const }
                    : item,
                ),
              },
            };
          });
        });
      }
    };
    document.addEventListener("visibilitychange", flush);
    window.addEventListener("pagehide", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
      window.removeEventListener("pagehide", flush);
    };
  }, [stage2Loaded]);

  useEffect(() => {
    if (
      "serviceWorker" in navigator &&
      import.meta.env.PROD &&
      !portableReview
    ) {
      navigator.serviceWorker
        .register(`${import.meta.env.BASE_URL}sw.js`)
        .catch(() => undefined);
    }
  }, []);

  const scopedDescription = useMemo(
    () =>
      draft.scopedDimensions.length
        ? `Invitation scope: ${draft.scopedDimensions.join(", ")}`
        : "Full catalogue",
    [draft.scopedDimensions],
  );

  const startNewDraft = async () => {
    await deleteDraft().catch(() => undefined);
    setDraft(
      createDraft(indicatorCatalogue.catalogueVersion, FORM_VERSION, []),
    );
    navigate("/a1/profile");
  };

  const completeLocalReview = () => {
    const timestamp = new Date();
    const reference = `A1-LOCAL-${timestamp
      .toISOString()
      .slice(0, 10)
      .replaceAll("-", "")}-${draft.submissionId.slice(0, 8).toUpperCase()}`;
    setDraft({
      ...draft,
      receipt: { reference, completedAt: timestamp.toISOString() },
    });
    navigate("/a1/receipt");
  };

  if (!loaded || !stage2Loaded) {
    return (
      <div className="loading-screen">
        <span className="brand__mark">WSI</span>
        <p>Opening your local draft…</p>
      </div>
    );
  }

  const isA1Route = route.startsWith("/a1/");
  const isStage2Route =
    route.startsWith("/a2/") ||
    route === "/dev/symbols" ||
    route === "/a3" ||
    route.startsWith("/a3/") ||
    route === "/engagements" ||
    route === "/send" ||
    route === "/details";
  const isStage3Route = route === "/stage3" || route.startsWith("/stage3/");
  return (
    <div className="app-shell">
      <AppHeader
        onHelp={() => setHelpOpen(true)}
        onHome={() => navigate("/")}
        showNavigation={route !== "/"}
      />
      {portableReview && (
        <aside className="complete-review-note" role="note">
          <strong>Complete remote review edition</strong>
          <span>
            Stages 1, 2 and 3 · browser-local training data only
          </span>
        </aside>
      )}
      {isA1Route && (
        <>
          <ContextBar
            draft={draft}
            saveState={saveState}
            online={online}
          />
          <div className="scope-banner">{scopedDescription}</div>
        </>
      )}
      {isStage2Route && (
        <>
          <div className="stage2-context">
            <strong aria-live="polite">
              {stage2SaveState === "error"
                ? "Local save failed — keep this page open and free browser storage"
                : stage2SaveState === "saving"
                  ? "Saving on this device…"
                  : "Saved on this device as you go"}
            </strong>
          </div>
          <Stage2Nav route={route} />
        </>
      )}

      {route === "/" && (
        <Landing draft={draft} onNewDraft={startNewDraft} />
      )}
      {route === "/a1/profile" && (
        <ProfileScreen
          draft={draft}
          onChange={(profile) =>
            setDraft({
              ...draft,
              profile,
              receipt: null,
              updatedAt: new Date().toISOString(),
            })
          }
        />
      )}
      {route === "/a1/items" && (
        <ItemsScreen
          draft={draft}
          onChange={(nextDraft) =>
            setDraft({ ...nextDraft, receipt: null })
          }
        />
      )}
      {route === "/a1/review" && (
        <ReviewScreen
          draft={draft}
          onComplete={completeLocalReview}
          onEdit={(index) => {
            setDraft({ ...draft, currentIndex: index });
            navigate("/a1/items");
          }}
        />
      )}
      {route === "/a1/receipt" && <ReceiptScreen draft={draft} />}
      {isStage2Route && (
        <Suspense
          fallback={
            <div className="loading-screen loading-screen--inline">
              <span className="brand__mark">WSI</span>
              <p>Opening Stage 2…</p>
            </div>
          }
        >
          <Stage2Router
            route={route}
            state={stage2State}
            onChange={(next) =>
              setStage2State({
                ...next,
                schemaVersion: 2,
                a2Draft: withDraftMetadata(next.a2Draft),
              })
            }
            navigate={navigate}
          />
        </Suspense>
      )}
      {isStage3Route && (
        <Suspense
          fallback={
            <div className="loading-screen loading-screen--inline">
              <span className="brand__mark">WSI</span>
              <p>Opening the controlled service…</p>
            </div>
          }
        >
          <Stage3Router
            route={route}
            state={stage2State}
            onChange={(next) =>
              setStage2State({
                ...next,
                schemaVersion: 2,
                a2Draft: withDraftMetadata(next.a2Draft),
              })
            }
            navigate={navigate}
          />
        </Suspense>
      )}
      {![
        "/",
        "/a1/profile",
        "/a1/items",
        "/a1/review",
        "/a1/receipt",
        "/a2/asset",
        "/a2/asset/map",
        "/a2/asset/evidence",
        "/a2/map",
        "/a2/import",
        "/a3",
        "/a3/official",
        "/a3/community",
        "/engagements",
        "/send",
        "/details",
        "/dev/symbols",
        "/stage3",
        "/stage3/sync",
        "/stage3/review",
        "/stage3/team-map",
        "/stage3/releases",
        "/stage3/operations",
      ].includes(
        route,
      ) && (
        <main className="not-found">
          <p className="eyebrow">Page not found</p>
          <h1>This route is not in the Stage 1 screen inventory.</h1>
          <button className="button button--primary" onClick={() => navigate("/")}>
            Return to assignments
          </button>
        </main>
      )}

      <footer>
        <span>WSI Student Support · Local-first capture · Stage 3 controlled service</span>
        <span>
          Catalogue {indicatorCatalogue.catalogueVersion} ·{" "}
          {indicatorCatalogue.sourceSha256.slice(0, 12)}…
        </span>
      </footer>
      {helpOpen && <HelpPanel onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
